import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useQuery} from 'react-query';
import {useLocation, useNavigate} from 'react-router-dom';

import {ApiQuery, ApiRuntime} from './gen/api';
import {AuthApi} from './gen/api/apis';
import {AuthMeResponse} from './gen/api/models';
import {BaseAPI, Middleware} from './gen/api/runtime';
import {HttpStatusCode} from './main/lib/net/HttpStatusCode';

type login = (newToken: string, userInfo: AuthMeResponse) => void;
type logout = () => void;

export interface AuthState {
    user: AuthMeResponse | null;
    login: login;
    logout: logout;
    loaded: boolean;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Requests a new access token. Resolves to null only when the server rejects the refresh token (the session is over);
 * rejects on network or server errors, so the caller keeps the session and tries again later.
 * Concurrent callers share one request.
 */
function refreshAccessToken(): Promise<string | null> {
    if (refreshInFlight === null) {
        refreshInFlight = new AuthApi()
            .postAdminRefreshAccessToken()
            .then((response) => {
                if (response.statusCode === HttpStatusCode.UNAUTHORIZED) {
                    return null;
                }
                if (response.statusCode !== HttpStatusCode.OK || !response.data?.token) {
                    throw new Error(`Token refresh failed with status ${response.statusCode ?? 'network error'}`);
                }
                return response.data.token;
            })
            .finally(() => {
                refreshInFlight = null;
            });
    }
    return refreshInFlight;
}

const isAuthUrl = (url: string) => url.includes('/login') || url.includes('/refresh-access-token');

export const AuthProvider: React.FC<{children?: React.ReactNode}> = ({children}) => {
    const [user, setUser] = useState<AuthMeResponse | null>(null);
    const [accessToken, setAccessToken] = useState(() => localStorage.getItem('token'));
    const [loaded, setLoaded] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const setupAccessToken = (token: string | null) => {
        setAccessToken(token);
        if (token === null) {
            localStorage.removeItem('token');
        } else {
            ApiRuntime.setApiKey(token);
            localStorage.setItem('token', token);
        }
    };

    useEffect(() => {
        const channel = new BroadcastChannel('auth');

        channel.onmessage = (event) => {
            if (event.data.type === 'ACCESS_TOKEN') {
                setupAccessToken(event.data.token);
            }
        };

        return () => {
            channel.close();
        };
    }, []);
    const accessTokenExists = () => accessToken !== null && accessToken !== '';
    const me = ApiQuery.useGetMeMutation({});

    // On a 401 the access token has most likely expired: refresh it once and retry the request.
    // Log out only when the refresh token is rejected; while the backend is unreachable the session is kept.
    useEffect(() => {
        const unauthorizedMiddleware: Middleware = {
            post: async (context) => {
                if (
                    context.response.status !== HttpStatusCode.UNAUTHORIZED ||
                    isAuthUrl(context.url) ||
                    !localStorage.getItem('token')
                ) {
                    return context.response;
                }

                let token: string | null;
                try {
                    token = await refreshAccessToken();
                } catch (error) {
                    console.error('Failed to refresh the access token, keeping the session:', error);
                    return context.response;
                }

                if (token === null) {
                    logout();
                    return context.response;
                }

                updateAccessToken(token);
                return fetch(context.url, {
                    ...context.init,
                    headers: {...(context.init.headers as Record<string, string>), Authorization: `Bearer ${token}`},
                });
            },
        };
        BaseAPI.middleware = BaseAPI.middleware.concat(unauthorizedMiddleware);

        return () => {
            BaseAPI.middleware = BaseAPI.middleware.filter((middleware) => middleware !== unauthorizedMiddleware);
        };
    }, []);

    useEffect(() => {
        if (!accessTokenExists()) {
            if (!location.pathname.includes('/login')) {
                navigate('/login');
            }
            setLoaded(true);
            return;
        }

        me.mutateAsync()
            .then((response) => {
                setUser(response);
            })
            .catch((error) => {
                // A rejected session is logged out by the middleware; other errors keep it.
                console.error('Failed to restore session:', error);
            })
            .finally(() => setLoaded(true));
    }, []);

    const updateAccessToken = (token: string) => {
        const channel = new BroadcastChannel('auth');
        channel.postMessage({type: 'ACCESS_TOKEN', token});
        channel.close();
        setupAccessToken(token);
    };

    useQuery(['refreshAccessToken'], refreshAccessToken, {
        refetchInterval: 30000,
        refetchIntervalInBackground: true,
        enabled: accessTokenExists() && loaded,
        refetchOnReconnect: true,
        refetchOnWindowFocus: true,
        staleTime: 0,
        onSuccess: (token) => {
            if (token === null) {
                logout();
                return;
            }
            updateAccessToken(token);
        },
        onError: (error) => {
            console.error('Failed to refresh the access token, keeping the session:', error);
        },
    });

    const login = useCallback((newToken: string, userInfo: AuthMeResponse) => {
        setupAccessToken(newToken);
        setUser(userInfo);
    }, []);

    const logout = useCallback(() => {
        setupAccessToken(null);
        setUser(null);
        localStorage.setItem('token', '');
        navigate('/login');
    }, []);

    const value = useMemo(() => ({user, login, logout, loaded}), [user, login, logout, loaded]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    return useContext(AuthContext);
};
